import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTour } from './Tour';
import { settingsApi } from '../../api/settingsApi';
import { legalApi } from '../../api/legalApi';
import { accountApi } from '../../api/accountApi';
import Onboarding from '../pages/Onboarding';
import { AccountDeletedGate } from './AccountDeletedGate';
import { ChangelogAlert } from './ChangelogAlert';

const guardStyles: Record<string, React.CSSProperties> = {
  loader: { padding: 24, textAlign: 'center', justifyContent: 'center' }
};

export function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const accountQ = useQuery({ queryKey: ['account'], queryFn: () => accountApi.getState() });
  const providersQ = useQuery({ queryKey: ['providers'], queryFn: () => settingsApi.listProviders() });
  const legalQ = useQuery({ queryKey: ['legal'], queryFn: () => legalApi.getStatus() });
  const { stop } = useTour();

  // Ждём ОБА запроса: при быстром providers и медленном legal статус согласия
  // ещё неизвестен, и тур стартовал поверх экрана ToS.
  const dataReady = !!providersQ.data && !!legalQ.data;
  const hasEnvProvider = !!providersQ.data?.env_provider;
  const hasUserKey = !!providersQ.data?.providers?.some((p) => p.has_key);
  const needsConsent = legalQ.data?.needsConsent !== false; // пока грузится — считаем, что нужно
  const needsOnboarding = dataReady && (needsConsent || (!hasEnvProvider && !hasUserKey));

  // Онбординг — поток из нескольких шагов, а не одно условие. Раньше он
  // размонтировался сразу после принятия ToS (согласие получено, ключи
  // платформы есть), поэтому «Добро пожаловать» мелькало и сразу шёл тур.
  // Теперь поток живёт до явного завершения через onDone.
  const [flowActive, setFlowActive] = useState(false);
  const flowDone = useRef(false);

  useEffect(() => {
    if (!dataReady) return;
    if (needsConsent) {
      // Согласие сброшено («Начать заново», новая версия ToS) — показываем поток заново.
      flowDone.current = false;
      setFlowActive(true);
      return;
    }
    if (needsOnboarding && !flowDone.current) setFlowActive(true);
  }, [dataReady, needsConsent, needsOnboarding]);

  // Пока идёт онбординг — тур не запускаем и гасим уже запущенный.
  useEffect(() => {
    if (!flowActive) return;
    stop();
  }, [flowActive, stop]);


  if (accountQ.isLoading || providersQ.isLoading || legalQ.isLoading) {
    return <div className="tma2-root" style={guardStyles.loader}>Загрузка...</div>;
  }

  // Удалённый аккаунт в пределах срока хранения — экран восстановления / начать заново.
  if (accountQ.data?.deleted) {
    return <AccountDeletedGate state={accountQ.data} />;
  }

  if (accountQ.isError || providersQ.isError || legalQ.isError) {
    return <div className="tma2-root" style={guardStyles.loader} role="alert">
      Не удалось проверить доступ. <button onClick={() => { void accountQ.refetch(); void providersQ.refetch(); void legalQ.refetch(); }}>Повторить</button>
    </div>;
  }


  if (flowActive || needsOnboarding) {
    return <Onboarding onDone={() => { flowDone.current = true; setFlowActive(false); }} />;
  }

  return (
    <>
      <ChangelogAlert />
      {children}
    </>
  );
}
